import { motion } from 'framer-motion'
import { GraduationCap, Award, MapPin } from 'lucide-react'
import { wins } from '../data/portfolioData'

const inView = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
}

const values = [
  { title: "Dependable", desc: "Systems don't sleep. My commitment to uptime and reliability runs 24/7." },
  { title: "Integrity", desc: "Clear on what I can deliver. Transparent when I can't. No surprises." },
  { title: "Loyalty", desc: "I build for the long term — your systems, your team, your growth." },
]

const education = [
  { icon: GraduationCap, title: "B.Tech — Information Technology", org: "Ajeenkya DY Patil University", year: "2017 — 2021" },
  { icon: GraduationCap, title: "Masters — Applied IT", org: "Victoria University, Melbourne", year: "2024 — Present" },
  { icon: Award, title: "Dell EMC Python Associate", org: "Certified", year: "2023" },
]

export default function About() {
  return (
    <section id="about" className="bg-[#FAFAFA]">

      {/* Editorial dark marquee strip */}
      <div className="border-y border-[#111111] bg-[#111111] overflow-hidden py-3.5">
        <motion.div
          animate={{ x: '-50%' }}
          transition={{ duration: 40, repeat: Infinity, ease: 'linear', repeatType: 'loop' }}
          className="flex whitespace-nowrap"
        >
          {[...wins, ...wins].map((item, i) => (
            <span key={i} className="font-mono text-[11px] tracking-[0.25em] text-white/55 uppercase mx-8">
              <span className="text-[#0047AB] mr-4">·</span>
              {item}
            </span>
          ))}
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 md:py-24">

        {/* Section label */}
        <motion.div {...inView} className="flex items-center gap-3 mb-10">
          <span className="font-mono text-[#0047AB] text-xs tracking-[0.3em] uppercase">About</span>
          <span className="h-px flex-1 max-w-[60px] bg-[#0047AB]/25" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 lg:gap-24 items-start">

          {/* Left */}
          <div>
            <motion.h2
              {...inView}
              className="font-sans font-bold text-4xl md:text-5xl leading-tight tracking-tight text-[#111111] mb-8"
            >
              IT professional.
              <br />
              <span className="text-[#6B7280]">Global operator.</span>
            </motion.h2>

            <motion.p
              {...inView}
              transition={{ ...inView.transition, delay: 0.08 }}
              className="font-body text-[#6B7280] text-base md:text-lg leading-relaxed mb-5"
            >
              I specialize in the full IT lifecycle — scoping, procurement, installation, and support.
              Whether it's a new store needing CCTV and POS, or a supermarket group needing a custom
              CRM, I treat every project as if the business depends on it. Because it does.
            </motion.p>

            <motion.p
              {...inView}
              transition={{ ...inView.transition, delay: 0.14 }}
              className="font-body text-[#6B7280] text-base md:text-lg leading-relaxed mb-10"
            >
              Across three countries and a decade of IT, I've moved from enterprise helpdesk to
              critical national infrastructure to full-stack implementation — always driven by the
              same principle: technology should just work.
            </motion.p>

            {/* Values */}
            <div className="space-y-3">
              {values.map((v, i) => (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="flex gap-4 p-5 border border-[#E5E7EB] rounded-xl bg-white shadow-card hover:border-[#0047AB]/20 hover:shadow-card-hover transition-all duration-300"
                >
                  <span className="font-mono text-[#0047AB] text-xs mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <div className="font-sans font-semibold text-[#111111] text-sm mb-1">{v.title}</div>
                    <div className="font-body text-[#6B7280] text-sm leading-relaxed">{v.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right */}
          <div>
            <motion.h3
              {...inView}
              className="font-mono text-[#0047AB] text-xs uppercase tracking-[0.3em] mb-6"
            >
              Education &amp; Credentials
            </motion.h3>

            <div className="space-y-3 mb-12">
              {education.map((e, i) => (
                <motion.div
                  key={e.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="flex gap-4 p-5 border border-[#E5E7EB] rounded-xl bg-white shadow-card hover:border-[#0047AB]/20 hover:shadow-card-hover transition-all duration-300"
                >
                  <div className="p-2 bg-[#EBF2FF] border border-[#0047AB]/15 rounded-lg shrink-0">
                    <e.icon size={14} className="text-[#0047AB]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-sans font-semibold text-[#111111] text-sm mb-0.5">{e.title}</div>
                    <div className="font-body text-[#6B7280] text-xs">{e.org}</div>
                  </div>
                  <span className="font-mono text-[#9CA3AF] text-xs shrink-0">{e.year}</span>
                </motion.div>
              ))}
            </div>

            <motion.h3
              {...inView}
              className="font-mono text-[#0047AB] text-xs uppercase tracking-[0.3em] mb-5"
            >
              Outside the Terminal
            </motion.h3>

            <motion.div
              {...inView}
              transition={{ ...inView.transition, delay: 0.15 }}
              className="flex flex-wrap gap-2 mb-8"
            >
              {['Reading', 'Cooking', 'Travel', 'Live Music', 'Gym'].map((interest) => (
                <span
                  key={interest}
                  className="px-3 py-1.5 border border-[#E5E7EB] rounded-full text-[#6B7280] text-xs font-body bg-white hover:border-[#0047AB]/30 hover:text-[#0047AB] transition-all duration-200"
                >
                  {interest}
                </span>
              ))}
            </motion.div>

            <motion.div
              {...inView}
              transition={{ ...inView.transition, delay: 0.2 }}
              className="flex items-center gap-2 text-[#6B7280] text-sm font-body"
            >
              <MapPin size={14} className="text-[#0047AB]" />
              Based in Melbourne, Australia · Open to remote &amp; on-site
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}
